import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { serviceImages } from '@/mocks/salonData';
import { videoIcons } from '@/mocks/videoDetails';

interface ServiceItem {
  title: string;
  desc: string;
  badge?: string;
}

interface DetailData {
  title: string;
  subtitle: string;
  duration: string;
  resultType: string;
  guarantee: string;
  socialProof: string;
  socialProofSub: string;
  description: string;
  advantages: string[];
  targetAudience: string[];
}

function ServiceDetailModal({
  isOpen,
  onClose,
  idx,
}: {
  isOpen: boolean;
  onClose: () => void;
  idx: number;
}) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const detail: DetailData = {
    title: t(`videoDetails.items.${idx}.title`),
    subtitle: t(`videoDetails.items.${idx}.subtitle`),
    duration: t(`videoDetails.items.${idx}.duration`),
    resultType: t(`videoDetails.items.${idx}.resultType`),
    guarantee: t(`videoDetails.items.${idx}.guarantee`),
    socialProof: t(`videoDetails.items.${idx}.socialProof`),
    socialProofSub: t(`videoDetails.items.${idx}.socialProofSub`),
    description: t(`videoDetails.items.${idx}.description`),
    advantages: t(`videoDetails.items.${idx}.advantages`, { returnObjects: true }) as string[],
    targetAudience: t(`videoDetails.items.${idx}.targetAudience`, { returnObjects: true }) as string[],
  };

  const icons = videoIcons[idx + 1] ?? { iconResult: 'ri-heart-line', iconGuarantee: 'ri-shield-check-line' };
  const askMsg = t('videoDetails.askMessage', { title: detail.title });
  const message = encodeURIComponent(askMsg);

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 z-[100] flex items-center justify-center animate-fade-in"
      onClick={(e) => {
        if (e.target === modalRef.current) onClose();
      }}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div className="relative z-10 bg-white rounded-2xl overflow-hidden max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto animate-scale-in">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-lg" />
        </button>

        {/* Image */}
        <div className="relative aspect-video bg-dark-950 flex items-center justify-center">
          <img
            src={serviceImages[idx + 1]}
            alt={detail.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/20" />
        </div>

        {/* Content */}
        <div className="p-5 md:p-7 space-y-5">
          <div>
            <h3 className="text-xl md:text-2xl font-bold text-dark-950">{detail.title}</h3>
            <p className="text-dark-500 text-sm md:text-base mt-1">{detail.subtitle}</p>
          </div>

          {/* 3 Info Cards */}
          <div className="grid grid-cols-3 gap-2 md:gap-3">
            <div className="bg-blue-50 rounded-xl p-3 flex items-center gap-2 md:gap-3">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-time-line text-blue-600 text-base md:text-lg" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] md:text-xs text-blue-600 font-medium truncate">{t('videoDetails.durationLabel')}</p>
                <p className="text-xs md:text-sm font-semibold text-blue-800 truncate">{detail.duration}</p>
              </div>
            </div>
            <div className="bg-purple-50 rounded-xl p-3 flex items-center gap-2 md:gap-3">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                <i className={`${icons.iconResult} text-purple-600 text-base md:text-lg`} />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] md:text-xs text-purple-600 font-medium truncate">{t('videoDetails.resultLabel')}</p>
                <p className="text-xs md:text-sm font-semibold text-purple-800 truncate">{detail.resultType}</p>
              </div>
            </div>
            <div className="bg-emerald-50 rounded-xl p-3 flex items-center gap-2 md:gap-3">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <i className={`${icons.iconGuarantee} text-emerald-600 text-base md:text-lg`} />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] md:text-xs text-emerald-600 font-medium truncate">{t('videoDetails.guaranteeLabel')}</p>
                <p className="text-xs md:text-sm font-semibold text-emerald-800 truncate">{detail.guarantee}</p>
              </div>
            </div>
          </div>

          {/* Social Proof */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-fire-line text-amber-600 text-lg md:text-xl" />
            </div>
            <div>
              <p className="text-sm md:text-base font-semibold text-amber-800">{detail.socialProof}</p>
              <p className="text-xs md:text-sm text-amber-600">{detail.socialProofSub}</p>
            </div>
          </div>

          {/* Details */}
          <div>
            <h4 className="text-base md:text-lg font-bold text-dark-950 mb-2">{t('videoDetails.detailsTitle')}</h4>
            <p className="text-dark-500 text-sm md:text-base leading-relaxed">{detail.description}</p>
          </div>

          {/* Advantages */}
          <div>
            <h4 className="text-base md:text-lg font-bold text-dark-950 mb-3">{t('videoDetails.advantagesTitle')}</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {detail.advantages.map((adv, i) => (
                <div key={i} className="flex items-start gap-2 bg-dark-50 rounded-lg p-3">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-100 flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-emerald-600 text-xs" />
                  </div>
                  <span className="text-sm text-dark-700 leading-snug">{adv}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Target Audience */}
          <div>
            <h4 className="text-base md:text-lg font-bold text-dark-950 mb-3">{t('videoDetails.targetAudienceTitle')}</h4>
            <div className="flex flex-wrap gap-2">
              {detail.targetAudience.map((ta, i) => (
                <span key={i} className="px-3 md:px-4 py-2 rounded-full bg-champagne-100 text-champagne-800 text-xs md:text-sm font-medium">
                  {ta}
                </span>
              ))}
            </div>
          </div>

          {/* WhatsApp CTA */}
          <a
            href={`https://wa.me/905313459987?text=${message}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full bg-champagne-500 text-dark-950 py-3.5 rounded-full font-semibold text-sm md:text-base hover:bg-champagne-400 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-line text-lg" />
            {t('videos.askWhatsApp')}
          </a>
        </div>
      </div>
    </div>
  );
}

export default function ServicesSection() {
  const { t } = useTranslation();
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const message = encodeURIComponent(t('whatsapp.message'));
  const items = t('services.items', { returnObjects: true }) as ServiceItem[];

  const openModal = (idx: number) => {
    setActiveIdx(idx);
    setIsModalOpen(true);
  };

  return (
    <section id="services" className="py-16 md:py-24 bg-cream-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <p className="text-champagne-600 text-xs font-medium tracking-[0.2em] uppercase mb-3">
            {t('services.title')}
          </p>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-dark-950 mb-3">
            {t('services.title')}
          </h2>
          <p className="text-dark-500 text-base md:text-lg max-w-xl mx-auto">
            {t('services.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {items.map((item, idx) => {
            const isFeatured = idx === 0;
            return (
              <div
                key={idx}
                className={`group relative rounded-2xl overflow-hidden bg-white border transition-all hover:shadow-xl ${
                  isFeatured
                    ? 'border-champagne-400 ring-2 ring-champagne-400/20 md:scale-105 md:z-10'
                    : 'border-dark-100 hover:border-champagne-300'
                }`}
              >
                {isFeatured && item.badge && (
                  <div className="absolute top-4 right-4 z-10 bg-champagne-500 text-dark-950 text-xs font-bold px-3 py-1 rounded-full">
                    {item.badge}
                  </div>
                )}
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={serviceImages[idx + 1]}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                </div>
                <div className="p-5 md:p-6">
                  <h3 className="text-dark-950 font-bold text-lg md:text-xl mb-2">
                    {item.title}
                  </h3>
                  <p className="text-dark-500 text-sm md:text-base leading-relaxed mb-5">
                    {item.desc}
                  </p>

                  {/* Buttons */}
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => openModal(idx)}
                      className="flex items-center justify-center gap-2 w-full py-3 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer bg-dark-950 text-white hover:bg-dark-800"
                    >
                      <i className="ri-arrow-right-line" />
                      {t('services.viewDetails')}
                    </button>
                    <a
                      href={`https://wa.me/905313459987?text=${message}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center justify-center gap-2 w-full py-3 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                        isFeatured
                          ? 'bg-champagne-500 text-dark-950 hover:bg-champagne-400'
                          : 'border border-dark-200 text-dark-700 hover:bg-dark-950 hover:text-white hover:border-dark-950'
                      }`}
                    >
                      <i className="ri-whatsapp-line" />
                      {t('services.askPrice')}
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <ServiceDetailModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        idx={activeIdx ?? 0}
      />
    </section>
  );
}